<template>
  <div class="ai-progress-ring" :class="`ai-progress-ring--${state}`">
    <div class="ring-halo ring-halo--outer"></div>
    <div class="ring-halo ring-halo--inner"></div>
    <svg viewBox="0 0 220 220" class="ring-svg" aria-hidden="true">
      <defs>
        <linearGradient id="aiRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#eaf4ff" />
          <stop offset="28%" stop-color="#9db8ff" />
          <stop offset="62%" stop-color="#5b8cff" />
          <stop offset="100%" stop-color="#72d8ff" />
        </linearGradient>
      </defs>
      <circle class="ring-track" cx="110" cy="110" r="84" />
      <circle class="ring-markers" cx="110" cy="110" r="92" />
      <circle
        class="ring-progress"
        cx="110"
        cy="110"
        r="84"
        :style="progressStyle"
      />
    </svg>
    <div class="ring-copy">
      <div class="ring-number">
        <span>{{ progress }}</span>
        <small>%</small>
      </div>
      <p>{{ caption }}</p>
      <div class="ring-signal">
        <i v-for="bar in 9" :key="bar"></i>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from 'vue';

type VerificationState = 'idle' | 'verifying' | 'verified' | 'error';

export default defineComponent({
  name: 'AiProgressRing',
  props: {
    progress: {
      type: Number,
      required: true,
    },
    caption: {
      type: String,
      required: true,
    },
    state: {
      type: String as PropType<VerificationState>,
      required: true,
    },
  },
  setup(props) {
    const circumference = 2 * Math.PI * 84;
    const progressStyle = computed(() => ({
      strokeDasharray: `${circumference}`,
      strokeDashoffset: `${circumference * (1 - props.progress / 100)}`,
    }));

    return {
      progressStyle,
    };
  },
});
</script>

<style scoped>
.ai-progress-ring {
  position: relative;
  width: clamp(228px, 28vw, 360px);
  aspect-ratio: 1;
  display: grid;
  place-items: center;
}

.ring-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
  filter: drop-shadow(0 0 28px rgba(114, 216, 255, 0.22));
}

.ring-track {
  fill: none;
  stroke: rgba(177, 208, 255, 0.28);
  stroke-width: 10;
}

.ring-markers {
  fill: none;
  stroke: rgba(175, 203, 247, 0.55);
  stroke-width: 1;
  stroke-dasharray: 2 6;
}

.ring-progress {
  fill: none;
  stroke: url(#aiRingGradient);
  stroke-width: 12;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.3s ease;
  transform-origin: center;
  animation: ringSpin 9s linear infinite;
}

.ring-halo {
  position: absolute;
  border-radius: 999px;
  pointer-events: none;
}

.ring-halo--outer {
  inset: 8%;
  border: 1px solid rgba(197, 224, 255, 0.6);
  box-shadow:
    0 0 30px rgba(114, 216, 255, 0.18),
    inset 0 0 24px rgba(255, 255, 255, 0.7);
  animation: haloPulse 3s ease-in-out infinite;
}

.ring-halo--inner {
  inset: 19%;
  border: 1px solid rgba(153, 193, 255, 0.42);
}

.ring-copy {
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.ring-number {
  display: flex;
  align-items: baseline;
  gap: 4px;
  color: #2956a8;
}

.ring-number span {
  font-size: clamp(60px, 7vw, 90px);
  font-weight: 500;
  letter-spacing: -0.08em;
}

.ring-number small {
  font-size: clamp(22px, 2vw, 30px);
  font-weight: 600;
}

.ring-copy p {
  margin: 6px 0 0;
  color: #5576ad;
  font-size: clamp(16px, 1.4vw, 22px);
  letter-spacing: 0.05em;
}

.ring-signal {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  margin-top: 16px;
  height: 14px;
}

.ring-signal i {
  width: 3px;
  border-radius: 999px;
  background: linear-gradient(180deg, #9bc0ff, #5b8cff);
  animation: signalBeat 1.4s ease-in-out infinite;
}

.ring-signal i:nth-child(1),
.ring-signal i:nth-child(9) {
  height: 4px;
  animation-delay: 0s;
}

.ring-signal i:nth-child(2),
.ring-signal i:nth-child(8) {
  height: 7px;
  animation-delay: 0.12s;
}

.ring-signal i:nth-child(3),
.ring-signal i:nth-child(7) {
  height: 10px;
  animation-delay: 0.24s;
}

.ring-signal i:nth-child(4),
.ring-signal i:nth-child(6) {
  height: 12px;
  animation-delay: 0.36s;
}

.ring-signal i:nth-child(5) {
  height: 14px;
  animation-delay: 0.48s;
}

.ai-progress-ring--idle .ring-progress,
.ai-progress-ring--error .ring-progress {
  animation: none;
  opacity: 0.72;
}

.ai-progress-ring--idle .ring-halo--outer,
.ai-progress-ring--error .ring-halo--outer {
  animation: none;
  opacity: 0.65;
}

.ai-progress-ring--idle .ring-signal i,
.ai-progress-ring--error .ring-signal i {
  animation: none;
  opacity: 0.5;
}

@keyframes ringSpin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes haloPulse {
  0%,
  100% {
    opacity: 0.75;
    transform: scale(0.99);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
}

@keyframes signalBeat {
  0%,
  100% {
    opacity: 0.45;
    transform: scaleY(0.82);
  }
  50% {
    opacity: 1;
    transform: scaleY(1.1);
  }
}

@media (max-width: 768px) {
  .ai-progress-ring {
    width: min(100%, 260px);
  }

  .ring-number span {
    font-size: 56px;
  }

  .ring-number small {
    font-size: 20px;
  }
}

@media (max-width: 768px) and (orientation: portrait) {
  .ai-progress-ring {
    width: min(100%, 138px);
  }

  .ring-track {
    stroke-width: 9;
  }

  .ring-progress {
    stroke-width: 10;
  }

  .ring-number span {
    font-size: 38px;
  }

  .ring-number small {
    font-size: 14px;
  }

  .ring-copy p {
    margin-top: 3px;
    font-size: 10px;
  }

  .ring-signal {
    margin-top: 6px;
    height: 10px;
    gap: 3px;
  }

  .ring-signal i:nth-child(1),
  .ring-signal i:nth-child(9) {
    height: 3px;
  }

  .ring-signal i:nth-child(2),
  .ring-signal i:nth-child(8) {
    height: 5px;
  }

  .ring-signal i:nth-child(3),
  .ring-signal i:nth-child(7) {
    height: 7px;
  }

  .ring-signal i:nth-child(4),
  .ring-signal i:nth-child(6) {
    height: 8px;
  }

  .ring-signal i:nth-child(5) {
    height: 10px;
  }
}

@media (orientation: landscape) and (max-height: 820px) {
  .ai-progress-ring {
    width: clamp(170px, 20vw, 240px);
  }

  .ring-number span {
    font-size: clamp(38px, 4vw, 56px);
  }

  .ring-number small {
    font-size: 16px;
  }

  .ring-copy p {
    font-size: 12px;
  }

  .ring-signal {
    margin-top: 10px;
  }
}
</style>
